import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Paper,
  Stack,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  School as SchoolIcon,
  CheckCircle as ConnectedIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as TokenErrorIcon,
  AccountBalanceWallet as WalletIcon,
  WarningAmber as LowBalanceIcon,
  TrendingUp as MessagesIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Description as TemplateIcon,
  LibraryBooks as MasterIcon,
  Search as SearchIcon,
  Cancel as InactiveIcon,
  Assessment as ReportsIcon,
  History as AuditIcon,
  Subscriptions as SubIcon,
  ReportProblem as QualityIcon,
  Payments as RevenueIcon,
  Send as SendIcon,
  Bolt as BoltIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PageContentLayout from "../components/layout/PageContentLayout";
import WhatsAppSchoolsTable from "../components/whatsapp/admin/WhatsAppSchoolsTable";
import WhatsAppSchoolDetailDrawer from "../components/whatsapp/admin/WhatsAppSchoolDetailDrawer";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

/**
 * Manage WhatsApp Accounts — Tech Provider dashboard KPIs/submenus
 * merged with Admin CRM schools table + detail drawer.
 */

const emptyForm = {
  school_code: "",
  school_name: "",
  waba_id: "",
  phone_number_id: "",
  display_phone_number: "",
  verified_name: "",
  business_id: "",
  connection_status: "connected",
  activate_service: true,
  remark: "",
};

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "connected", label: "Connected" },
  { value: "pending", label: "Pending" },
  { value: "disabled", label: "Disabled" },
  { value: "token_error", label: "Token error" },
];

const QUALITY_OPTIONS = [
  { value: "all", label: "All quality" },
  { value: "GREEN", label: "Green" },
  { value: "YELLOW", label: "Yellow" },
  { value: "RED", label: "Red" },
  { value: "FLAGGED", label: "Flagged (Y/R)" },
];

const WA = crmColors.whatsapp;
const WA_DARK = "#1DA851";
const HEADER_BG = alpha(WA, 0.08);

/** EduOK yellow pill — matches BackButton / IRCT chrome */
const YELLOW = {
  light: "#fff8e1",
  mid: "#ffecb3",
  main: "#ffd54f",
  strong: "#ffc107",
  border: "#ffe082",
  text: "#5c4a00",
};

const formatCurrency = (n) =>
  `₹${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const HEADER_BTN_SX = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.78rem",
  letterSpacing: "0.01em",
  lineHeight: 1.25,
  minHeight: 34,
  px: 1.35,
  py: 0.45,
  borderRadius: "8px",
  borderWidth: "1.5px",
  boxShadow: "none",
  transition: "transform 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease, border-color 0.16s ease",
  "& .MuiButton-startIcon": {
    marginRight: 0.55,
    marginLeft: -0.1,
    "& > *:nth-of-type(1)": { fontSize: 15 },
  },
  "&:hover": {
    transform: "translateY(-1px)",
  },
};

const QUICK_TONE = {
  green: {
    cardBg: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
    border: "#dcfce7",
    hoverBorder: "#22c55e",
    hoverShadow: "0 4px 12px rgba(34, 197, 94, 0.15)",
    iconBg: "linear-gradient(135deg, #22c55e, #16a34a)",
  },
  blue: {
    cardBg: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)",
    border: "#dbeafe",
    hoverBorder: "#3b82f6",
    hoverShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
    iconBg: "linear-gradient(135deg, #3b82f6, #2563eb)",
  },
  indigo: {
    cardBg: "linear-gradient(135deg, #eef2ff 0%, #ffffff 100%)",
    border: "#e0e7ff",
    hoverBorder: "#6366f1",
    hoverShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
    iconBg: "linear-gradient(135deg, #6366f1, #4f46e5)",
  },
  orange: {
    cardBg: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
    border: "#ffedd5",
    hoverBorder: "#f97316",
    hoverShadow: "0 4px 12px rgba(249, 115, 22, 0.15)",
    iconBg: "linear-gradient(135deg, #f97316, #ea580c)",
  },
  violet: {
    cardBg: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 100%)",
    border: "#ede9fe",
    hoverBorder: "#8b5cf6",
    hoverShadow: "0 4px 12px rgba(139, 92, 246, 0.15)",
    iconBg: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
  },
  slate: {
    cardBg: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
    border: "#e2e8f0",
    hoverBorder: "#64748b",
    hoverShadow: "0 4px 12px rgba(100, 116, 139, 0.15)",
    iconBg: "linear-gradient(135deg, #64748b, #475569)",
  },
  wa: {
    cardBg: "linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)",
    border: "#bbf7d0",
    hoverBorder: "#25d366",
    hoverShadow: "0 4px 12px rgba(37, 211, 102, 0.18)",
    iconBg: "linear-gradient(135deg, #25d366, #128c7e)",
  },
  amber: {
    cardBg: "linear-gradient(135deg, #fffbeb 0%, #ffffff 100%)",
    border: "#fde68a",
    hoverBorder: "#f59e0b",
    hoverShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
    iconBg: "linear-gradient(135deg, #f59e0b, #d97706)",
  },
};

/** EduOKWeb adm-kpi__quick-item */
const QuickActionItem = memo(function QuickActionItem({ label, icon, tone = "blue", onClick }) {
  const t = QUICK_TONE[tone] || QUICK_TONE.blue;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      className={`adm-kpi__quick-item is-${tone}`}
      sx={{
        appearance: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
        p: "10px 8px",
        borderRadius: "10px",
        border: `1px solid ${t.border}`,
        background: t.cardBg,
        color: "#111827",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: t.hoverBorder,
          boxShadow: t.hoverShadow,
        },
      }}
    >
      <Box
        className="adm-kpi__quick-icon"
        aria-hidden
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          background: t.iconBg,
          boxShadow: "0 2px 6px rgba(15, 23, 42, 0.18)",
          "& .MuiSvgIcon-root": { fontSize: 15 },
        }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.2,
          color: "#111827",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
});

const HubTile = memo(function HubTile({
  title,
  hint,
  value,
  icon: Icon,
  circle,
  hoverBorder,
  active,
  index = 0,
  onClick,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={title}
      title={hint || title}
      sx={{
        "--hub-i": index,
        appearance: "none",
        width: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        textAlign: "left",
        gap: 1,
        p: "8px 10px",
        minHeight: 52,
        borderRadius: "10px",
        bgcolor: active ? alpha(hoverBorder, 0.08) : "#fff",
        border: `1px solid ${active ? hoverBorder : alpha("#94a3b8", 0.45)}`,
        color: "inherit",
        cursor: onClick ? "pointer" : "default",
        boxShadow: "none",
        transition: "transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
        animation: "waHubIn 0.28s ease both",
        animationDelay: `calc(var(--hub-i) * 25ms)`,
        "@keyframes waHubIn": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover, &:focus-visible": onClick
          ? {
              borderColor: hoverBorder,
              bgcolor: alpha(hoverBorder, 0.06),
              outline: "none",
            }
          : { outline: "none" },
      }}
    >
      <Box
        className="hub-circle"
        aria-hidden
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          flexShrink: 0,
          background: circle,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#64748b",
            lineHeight: 1.15,
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </Typography>
        {value != null && value !== "" && (
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

/** EduOKWeb adm-kpi__metric icon palette (cycling) */
const KPI_ICON_BG = ["#A26C02", "#5e35b1", "#00897b", "#E74900", "#455a64"];

/**
 * EduOKWeb-style KPI — matches .adm-kpi__metric (AdminMain.css)
 */
const KpiTile = memo(function KpiTile({
  title,
  value,
  icon,
  color,
  index = 0,
  prefix = "",
  suffix = "",
  hint,
  onClick,
}) {
  const iconBg = color || KPI_ICON_BG[index % KPI_ICON_BG.length];

  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className="adm-kpi__metric"
      sx={{
        appearance: "none",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.5,
        minHeight: 88,
        borderRadius: "12px",
        border: "2px solid rgba(3, 169, 244, 0.35)",
        background: "linear-gradient(165deg, #ffffff 0%, #fff6e0 100%)",
        boxShadow: "0 6px 16px rgba(2, 136, 209, 0.07)",
        color: "inherit",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "inherit",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-2px)",
              boxShadow: "0 10px 22px rgba(2, 136, 209, 0.12)",
            }
          : undefined,
      }}
    >
      <Box
        className="adm-kpi__metric-icon"
        aria-hidden
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: iconBg,
          color: "#fff",
          boxShadow: `0 4px 10px ${alpha(iconBg, 0.32)}`,
          "& .MuiSvgIcon-root": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box
        className="adm-kpi__metric-copy"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          minWidth: 0,
        }}
      >
        <Typography
          component="small"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: "#78909c",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          component="strong"
          sx={{
            fontSize: { xs: 18, md: 20 },
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1.15,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {prefix}
          {typeof value === "number" ? value.toLocaleString() : value}
          {suffix}
        </Typography>
        {hint ? (
          <Typography
            component="em"
            sx={{
              fontStyle: "normal",
              fontSize: 11,
              fontWeight: 600,
              color: "#64748b",
              lineHeight: 1.25,
            }}
          >
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
});

const SectionCard = memo(function SectionCard({ title, subtitle, icon, action, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "10px",
        border: `1px solid ${alpha(WA, 0.14)}`,
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: `1px solid ${alpha(WA, 0.12)}`,
          bgcolor: HEADER_BG,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(WA, 0.14),
              color: WA_DARK,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.84rem" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {action}
      </Box>
      <Box>{children}</Box>
    </Paper>
  );
});

const Empty = ({ text }) => (
  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2.5, textAlign: "center" }}>
    {text}
  </Typography>
);

const SUBMODULE_ROUTES = {
  "price-plans": "/whatsapp/price-plans",
  catalog: "/whatsapp/templates",
  "template-master": "/whatsapp/templates/master",
  reports: "/whatsapp/reports",
  audit: "/whatsapp/audit",
};

const ManageWhatsAppAccounts = () => {
  const navigate = useNavigate();
  const [subModule, setSubModule] = useState("accounts");

  const openSubModule = useCallback(
    (key) => {
      const route = SUBMODULE_ROUTES[key];
      if (route) {
        navigate(route);
        return;
      }
      setSubModule(key);
    },
    [navigate]
  );

  // Original dashboard KPIs / side panels
  const [dashLoading, setDashLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [lowBalance, setLowBalance] = useState([]);
  const [topSenders, setTopSenders] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [schoolTemplates, setSchoolTemplates] = useState({});
  const [walletTx, setWalletTx] = useState([]);
  const [service, setService] = useState({});

  // Admin CRM schools list
  const [adminOverview, setAdminOverview] = useState({});
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("all");
  const [quality, setQuality] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [activeCardKey, setActiveCardKey] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSchoolCode, setSelectedSchoolCode] = useState(null);

  // Create account
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setDashLoading(true);
    const result = await apiService.getWhatsAppDashboard({ recent_limit: 25 });
    if (!result.success || !result.data) {
      toast.error(result.error || "Failed to load school WhatsApp accounts");
      setDashLoading(false);
      return;
    }
    const d = result.data;
    setStats(d.stats || {});
    setLowBalance(Array.isArray(d.low_balance) ? d.low_balance : []);
    setTopSenders(Array.isArray(d.top_senders) ? d.top_senders : []);
    setBreakdown(Array.isArray(d.connection_breakdown) ? d.connection_breakdown : []);
    setSchoolTemplates(d.school_templates || {});
    setWalletTx(Array.isArray(d.wallet_transactions) ? d.wallet_transactions : []);
    setService(d.service || {});
    setDashLoading(false);
  }, []);

  const loadAdminOverview = useCallback(async () => {
    const result = await apiService.getWhatsAppAdminOverview();
    if (result.success) setAdminOverview(result.data || {});
  }, []);

  const loadSchoolsList = useCallback(async () => {
    setListLoading(true);
    const result = await apiService.getWhatsAppAdminSchools({
      search,
      status,
      quality,
      page: page + 1,
      limit: rowsPerPage,
    });
    if (result.success) {
      setRows(result.data || []);
      setTotal(result.total || 0);
    } else {
      toast.error(result.error || "Failed to load schools");
      setRows([]);
      setTotal(0);
    }
    setListLoading(false);
  }, [search, status, quality, page, rowsPerPage]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchDashboard(), loadAdminOverview(), loadSchoolsList()]);
  }, [fetchDashboard, loadAdminOverview, loadSchoolsList]);

  useEffect(() => {
    fetchDashboard();
    loadAdminOverview();
  }, [fetchDashboard, loadAdminOverview]);

  useEffect(() => {
    if (subModule === "accounts") loadSchoolsList();
  }, [subModule, loadSchoolsList]);

  const handleCardFilter = (cardKey, filter) => {
    if (!filter) return;
    setActiveCardKey(cardKey);
    setStatus(filter.status || "all");
    setQuality(filter.quality || "all");
    setPage(0);
    setSubModule("accounts");
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
    setActiveCardKey(null);
  };

  const loadSchoolOptions = useCallback(async (keyword = "") => {
    setSchoolsLoading(true);
    const result = keyword
      ? await apiService.searchSchools(keyword, "school_name")
      : await apiService.filterSchools({ limit: 50, offset: 0, order: "DESC" });
    if (result.success) {
      const list = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.schools)
          ? result.data.schools
          : [];
      setSchoolOptions(
        list
          .map((s) => ({
            school_code: s.school_code || s.School_Code || "",
            school_name: s.school_name || s.School_Name || "",
            admin_phone: s.admin_phone || s.Admin_Phone || "",
          }))
          .filter((s) => s.school_code)
      );
    } else {
      setSchoolOptions([]);
    }
    setSchoolsLoading(false);
  }, []);

  const openCreateDialog = async () => {
    setForm(emptyForm);
    setSelectedSchool(null);
    setDialogOpen(true);
    await loadSchoolOptions();
  };

  const handleCreate = async () => {
    if (!form.school_code || !form.waba_id || !form.phone_number_id) {
      toast.error("School, WABA ID and Phone Number ID are required");
      return;
    }
    setSaving(true);
    const result = await apiService.createWhatsAppAccount({
      school_code: form.school_code,
      waba_id: form.waba_id.trim(),
      phone_number_id: form.phone_number_id.trim(),
      display_phone_number: form.display_phone_number.trim() || null,
      verified_name: form.verified_name.trim() || form.school_name || null,
      business_id: form.business_id.trim() || null,
      connection_status: form.connection_status,
      activate_service: form.activate_service,
      remark: form.remark.trim() || undefined,
    });
    setSaving(false);
    if (result.success) {
      toast.success("School WhatsApp account saved");
      setDialogOpen(false);
      setForm(emptyForm);
      refreshAll();
    } else {
      toast.error(result.error || "Failed to create account");
    }
  };

  const handleToggleService = async (row) => {
    const nextStatus = String(row.service_status || "").toLowerCase() === "active" ? "Inactive" : "Active";
    const result = await apiService.updateWhatsAppServiceStatus({
      school_code: row.school_code,
      status: nextStatus,
      remark: "Toggled from Manage WhatsApp Accounts (Tech Provider)",
    });
    if (result.success) {
      toast.success(`WhatsApp service set to ${nextStatus}`);
      refreshAll();
    } else {
      toast.error(result.error || "Failed to update service status");
    }
  };

  const n = (v) => Number(v) || 0;
  const totalRegistered = Math.max(1, n(stats.total_registered));
  const maxTop = Math.max(1, ...topSenders.map((t) => n(t.message_count)), 1);

  return (
    <PageContentLayout
      title="WhatsApp Accounts"
      darkBar
      barSx={{ minHeight: 68, py: 1.75, px: 2.75 }}
      breadcrumbs={[
        { label: "WhatsApp Accounts", icon: <WhatsAppIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button
            variant="outlined"
            size="small"
            disableElevation
            startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
            onClick={refreshAll}
            sx={{
              ...HEADER_BTN_SX,
              bgcolor: alpha(YELLOW.light, 0.12),
              borderColor: alpha(YELLOW.border, 0.75),
              color: "#fff",
              backdropFilter: "blur(6px)",
              "&:hover": {
                bgcolor: alpha(YELLOW.mid, 0.32),
                borderColor: YELLOW.main,
                color: "#fff",
                boxShadow: `0 6px 16px -10px ${alpha(YELLOW.strong, 0.45)}`,
              },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={openCreateDialog}
            sx={{
              ...HEADER_BTN_SX,
              bgcolor: YELLOW.strong,
              borderColor: YELLOW.main,
              color: "#fff",
              boxShadow: `0 8px 20px -10px ${alpha(YELLOW.strong, 0.7)}`,
              "&:hover": {
                bgcolor: "#ffb300",
                borderColor: YELLOW.main,
                color: "#fff",
                boxShadow: `0 10px 22px -10px ${alpha(YELLOW.strong, 0.85)}`,
              },
            }}
          >
            Create Account
          </Button>
        </Stack>
      }
    >
      {dashLoading && listLoading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: WA }} />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {/* Primary KPIs — EduOKWeb adm-kpi__metrics--top */}
          <Box
            className="adm-kpi__metrics adm-kpi__metrics--top"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(5, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            <KpiTile
              index={0}
              title="Onboarded schools"
              value={n(stats.total_registered)}
              hint={`${n(stats.onboarded_this_month)} this month · ${n(stats.onboarded_today)} today`}
              icon={<SchoolIcon />}
              onClick={() => {
                setSubModule("accounts");
                setStatus("all");
                setQuality("all");
                setActiveCardKey(null);
              }}
            />
            <KpiTile
              index={1}
              title="Connected WABAs"
              value={n(stats.connected)}
              hint={`${n(service.active ?? stats.service_active)} service active · ${n(service.inactive)} inactive`}
              icon={<ConnectedIcon />}
              onClick={() => handleCardFilter("connected_wabas", { status: "connected", quality: "all" })}
            />
            <KpiTile
              index={2}
              title="Prepaid wallets"
              value={n(stats.total_wallet_balance)}
              prefix="₹"
              hint={`${n(stats.low_balance_schools)} below threshold`}
              icon={<WalletIcon />}
              onClick={() => setSubModule("accounts")}
            />
            <KpiTile
              index={3}
              title="Messages today"
              value={n(stats.messages_today)}
              hint={`${n(stats.messages_this_month)} this month · ${formatCurrency(stats.spend_this_month)} billed`}
              icon={<MessagesIcon />}
              onClick={() => setSubModule("accounts")}
            />
            <KpiTile
              index={4}
              title="Pending connect"
              value={n(stats.pending)}
              hint="Awaiting WABA link"
              icon={<PendingIcon />}
              onClick={() => handleCardFilter("pending_connect", { status: "pending", quality: "all" })}
            />
          </Box>

          {/* Overview submenu — compact filter tiles */}
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              borderRadius: "12px",
              border: `1px solid ${alpha("#94a3b8", 0.4)}`,
              bgcolor: "#fff",
            }}
          >
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={1}
              sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
            >
              <Typography
                sx={{
                  m: 0,
                  fontSize: "0.875rem",
                  fontWeight: 800,
                  color: "#0f172a",
                  letterSpacing: "-0.01em",
                }}
              >
                Overview & filters
              </Typography>
              <Typography
                sx={{
                  m: 0,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "#94a3b8",
                }}
              >
                Tap a tile to filter school accounts where available
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gap: 1,
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              {[
                {
                  key: "pending_connect",
                  title: "Pending connect",
                  hint: "Awaiting WABA link",
                  value: n(stats.pending),
                  icon: PendingIcon,
                  circle: "linear-gradient(145deg, #fbbf24 0%, #d97706 100%)",
                  hoverBorder: "#f59e0b",
                  filter: { status: "pending", quality: "all" },
                },
                {
                  key: "token_errors",
                  title: "Token errors",
                  hint: "Auth / token issues",
                  value: n(stats.token_error),
                  icon: TokenErrorIcon,
                  circle: "linear-gradient(145deg, #f87171 0%, #dc2626 100%)",
                  hoverBorder: "#ef4444",
                  filter: { status: "token_error", quality: "all" },
                },
                {
                  key: "failed_sends_today",
                  title: "Failed sends today",
                  hint: "Delivery failures",
                  value: n(stats.failed_messages_today),
                  icon: LowBalanceIcon,
                  circle: "linear-gradient(145deg, #fb7185 0%, #be123c 100%)",
                  hoverBorder: "#e11d48",
                  filter: null,
                },
                {
                  key: "school_templates",
                  title: "School templates",
                  hint: "Templates on schools",
                  value: n(schoolTemplates.total),
                  icon: TemplateIcon,
                  circle: "linear-gradient(145deg, #FDC85D 0%, #A26C02 100%)",
                  hoverBorder: "#DE9502",
                  filter: null,
                },
                {
                  key: "active_subscriptions",
                  title: "Active subscriptions",
                  hint: "Connected schools",
                  value: n(adminOverview.active_subscriptions),
                  icon: SubIcon,
                  circle: "linear-gradient(145deg, #F9A602 0%, #A26C02 100%)",
                  hoverBorder: "#A26C02",
                  filter: { status: "connected", quality: "all" },
                },
                {
                  key: "quality_flagged_schools",
                  title: "Quality flagged",
                  hint: "Yellow / red quality",
                  value: n(adminOverview.quality_flagged_schools),
                  icon: QualityIcon,
                  circle: "linear-gradient(145deg, #fb923c 0%, #ea580c 100%)",
                  hoverBorder: "#f97316",
                  filter: { status: "all", quality: "FLAGGED" },
                },
                {
                  key: "revenue_this_month",
                  title: "Revenue this month",
                  hint: "Billed this cycle",
                  value: formatCurrency(adminOverview.revenue_this_month).replace(/\.00$/, ""),
                  icon: RevenueIcon,
                  circle: "linear-gradient(145deg, #4ade80 0%, #16a34a 100%)",
                  hoverBorder: "#22c55e",
                  filter: null,
                },
                {
                  key: "messages_sent_this_month",
                  title: "Messages this month",
                  hint: "Outbound volume",
                  value: n(adminOverview.messages_sent_this_month),
                  icon: SendIcon,
                  circle: "linear-gradient(145deg, #818cf8 0%, #4f46e5 100%)",
                  hoverBorder: "#6366f1",
                  filter: null,
                },
                {
                  key: "templates_pending_approval",
                  title: "Templates pending",
                  hint: "Awaiting Meta approval",
                  value: n(adminOverview.templates_pending_approval),
                  icon: TemplateIcon,
                  circle: "linear-gradient(145deg, #a78bfa 0%, #7c3aed 100%)",
                  hoverBorder: "#8b5cf6",
                  filter: null,
                },
              ].map((item, index) => (
                <HubTile
                  key={item.key}
                  title={item.title}
                  hint={item.hint}
                  value={item.value}
                  icon={item.icon}
                  circle={item.circle}
                  hoverBorder={item.hoverBorder}
                  index={index}
                  active={activeCardKey === item.key}
                  onClick={
                    item.filter
                      ? () => handleCardFilter(item.key, item.filter)
                      : () => {
                          setActiveCardKey(item.key);
                          setSubModule("accounts");
                        }
                  }
                />
              ))}
            </Box>
          </Paper>

          {/* Quick actions — EduOKWeb adm-kpi__quick (below Overview & filters) */}
          <Box
            component="section"
            className="adm-kpi__quick"
            aria-label="Quick actions"
            sx={{
              borderRadius: "12px",
              border: "1.5px solid #e5e7eb",
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              p: "12px 14px 14px",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              className="adm-kpi__quick-head"
              sx={{ mb: 1.5, color: "#3b82f6" }}
            >
              <BoltIcon sx={{ fontSize: 18 }} />
              <Typography
                component="h2"
                sx={{ m: 0, fontSize: 15, fontWeight: 800, color: "#111827" }}
              >
                Quick actions
              </Typography>
            </Stack>
            <Box
              className="adm-kpi__quick-grid"
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))",
                gap: 1,
              }}
            >
              <QuickActionItem
                label="Price plans"
                tone="amber"
                icon={<WalletIcon />}
                onClick={() => openSubModule("price-plans")}
              />
              <QuickActionItem
                label="School Templates"
                tone="indigo"
                icon={<TemplateIcon />}
                onClick={() => openSubModule("catalog")}
              />
              <QuickActionItem
                label="Template Master"
                tone="blue"
                icon={<MasterIcon />}
                onClick={() => openSubModule("template-master")}
              />
              <QuickActionItem
                label="Reports"
                tone="slate"
                icon={<ReportsIcon />}
                onClick={() => openSubModule("reports")}
              />
              <QuickActionItem
                label="Audit Log"
                tone="violet"
                icon={<AuditIcon />}
                onClick={() => openSubModule("audit")}
              />
              <QuickActionItem
                label="Create Account"
                tone="wa"
                icon={<AddIcon />}
                onClick={openCreateDialog}
              />
            </Box>
          </Box>

          {subModule === "accounts" ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: `1px solid ${alpha(WA, 0.16)}`,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.35,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  borderBottom: `1px solid ${alpha(WA, 0.12)}`,
                  bgcolor: HEADER_BG,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(WA, 0.16),
                    color: WA_DARK,
                    flexShrink: 0,
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", lineHeight: 1.2 }}>
                    Account details
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    School WABA list, service toggle & portfolio panels
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={2} sx={{ p: 1.75 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.25}
                  alignItems={{ md: "center" }}
                >
                  <TextField
                    size="small"
                    placeholder="Search school name / code / phone / WABA"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(0);
                      setActiveCardKey(null);
                    }}
                    sx={{ minWidth: 160 }}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Quality"
                    value={quality}
                    onChange={(e) => {
                      setQuality(e.target.value);
                      setPage(0);
                      setActiveCardKey(null);
                    }}
                    sx={{ minWidth: 160 }}
                  >
                    {QUALITY_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: WA,
                      "&:hover": { bgcolor: WA_DARK },
                    }}
                  >
                    Search
                  </Button>
                </Stack>

              <WhatsAppSchoolsTable
                rows={rows}
                total={total}
                page={page}
                rowsPerPage={rowsPerPage}
                loading={listLoading}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                onRowClick={(row) => {
                  setSelectedSchoolCode(row.school_code);
                  setDrawerOpen(true);
                }}
              />

              {/* Quick service toggle strip for visible rows */}
              {rows.length > 0 && (
                <Box
                  sx={{ borderRadius: 2.5, border: `1px solid ${alpha(WA, 0.14)}`, overflow: "hidden" }}
                >
                  <Box sx={{ px: 1.75, py: 1, bgcolor: HEADER_BG, borderBottom: `1px solid ${alpha(WA, 0.12)}` }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Service quick toggle
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Activate / deactivate WhatsApp automation for schools on this page
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Connection</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Activated</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Expires</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">
                            Toggle
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => {
                          const active = String(row.service_status || "").toLowerCase() === "active";
                          const formatSvcDate = (v) => {
                            if (!v) return "—";
                            const d = new Date(v);
                            return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
                          };
                          return (
                            <TableRow key={`svc-${row.school_code}`}>
                              <TableCell>
                                <Typography fontWeight={650} variant="body2">
                                  {row.school_name || row.school_code}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.school_code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={row.connection_status || "—"} sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  icon={active ? <ConnectedIcon /> : <InactiveIcon />}
                                  label={row.service_status || (active ? "Active" : "InActive")}
                                  color={active ? "success" : "default"}
                                  variant={active ? "filled" : "outlined"}
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatSvcDate(row.service_activated_at)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatSvcDate(row.service_expires_at)}</Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 160 }}>
                                <Typography variant="body2" noWrap title={row.service_remark || ""}>
                                  {row.service_remark || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={active ? "Deactivate service" : "Activate service"}>
                                  <Switch
                                    size="small"
                                    checked={active}
                                    onChange={() => handleToggleService(row)}
                                    color="success"
                                  />
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </TableContainer>
                  </Box>
                )}

              {/* Original side panels */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <SectionCard title="Connection health" subtitle="Portfolio breakdown" icon={<ConnectedIcon fontSize="small" />}>
                      {breakdown.length === 0 ? (
                        <Empty text="No connection data" />
                      ) : (
                        breakdown.map((row) => (
                          <Box key={row.status} sx={{ px: 1.75, py: 1.1, borderBottom: `1px solid ${crmColors.divider}` }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={650} sx={{ textTransform: "capitalize" }}>
                                {String(row.status || "").replace(/_/g, " ")}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {n(row.count)} · {((n(row.count) / totalRegistered) * 100).toFixed(0)}%
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={(n(row.count) / totalRegistered) * 100}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(WA, 0.1),
                                "& .MuiLinearProgress-bar": { bgcolor: WA },
                              }}
                            />
                          </Box>
                        ))
                      )}
                    </SectionCard>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <SectionCard
                      title="Top sending schools"
                      subtitle="This month · messages & spend"
                      icon={<MessagesIcon fontSize="small" />}
                    >
                      {topSenders.length === 0 ? (
                        <Empty text="No school messages logged this month" />
                      ) : (
                        topSenders.map((row) => (
                          <Box
                            key={row.school_code}
                            sx={{ px: 1.75, py: 1.1, borderBottom: `1px solid ${crmColors.divider}` }}
                          >
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={650} noWrap sx={{ maxWidth: "55%" }}>
                                {row.school_name || row.school_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {n(row.message_count)} · {formatCurrency(row.total_spend)}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={(n(row.message_count) / maxTop) * 100}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(WA, 0.1),
                                "& .MuiLinearProgress-bar": { bgcolor: WA },
                              }}
                            />
                          </Box>
                        ))
                      )}
                    </SectionCard>

                    <SectionCard
                      title="Low wallet balance"
                      subtitle="Schools needing recharge"
                      icon={<LowBalanceIcon fontSize="small" />}
                    >
                      {lowBalance.length === 0 ? (
                        <Empty text="All school wallets look healthy" />
                      ) : (
                        lowBalance.map((row) => (
                          <Box
                            key={row.school_code}
                            sx={{ px: 1.75, py: 1.1, borderBottom: `1px solid ${crmColors.divider}` }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={650} noWrap>
                                  {row.school_name || row.school_code}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Threshold {formatCurrency(row.low_balance_threshold)}
                                </Typography>
                              </Box>
                              <Chip size="small" color="warning" label={formatCurrency(row.wallet_balance)} />
                            </Stack>
                          </Box>
                        ))
                      )}
                    </SectionCard>

                    <SectionCard
                      title="Wallet ledger"
                      subtitle="Recent recharges / debits"
                      icon={<WalletIcon fontSize="small" />}
                    >
                      {walletTx.length === 0 ? (
                        <Empty text="No wallet transactions" />
                      ) : (
                        walletTx.slice(0, 8).map((row) => (
                          <Box key={row.id} sx={{ px: 1.75, py: 1, borderBottom: `1px solid ${crmColors.divider}` }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={650} noWrap>
                                  {row.school_name || row.school_code}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {row.type} · {formatDate(row.created_at)}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color={
                                  ["recharge", "refund", "adjustment"].includes(row.type) && n(row.amount) >= 0
                                    ? "success.main"
                                    : "error.main"
                                }
                              >
                                {n(row.amount) >= 0 ? "+" : ""}
                                {formatCurrency(row.amount)}
                              </Typography>
                            </Stack>
                          </Box>
                        ))
                      )}
                    </SectionCard>
                  </Stack>
                </Grid>
              </Grid>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      )}

      <WhatsAppSchoolDetailDrawer
        open={drawerOpen}
        schoolCode={selectedSchoolCode}
        onClose={() => setDrawerOpen(false)}
      />

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create school WhatsApp account</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Link a school&apos;s WhatsApp Business Account that EduOK hosts as Tech Provider.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Autocomplete
              options={schoolOptions}
              loading={schoolsLoading}
              value={selectedSchool}
              getOptionLabel={(opt) =>
                opt.school_name ? `${opt.school_name} (${opt.school_code})` : opt.school_code || ""
              }
              isOptionEqualToValue={(a, b) => a?.school_code === b?.school_code}
              onInputChange={(_, value, reason) => {
                if (reason === "input") loadSchoolOptions(value);
              }}
              onChange={(_, value) => {
                setSelectedSchool(value);
                setForm((prev) => ({
                  ...prev,
                  school_code: value?.school_code || "",
                  school_name: value?.school_name || "",
                  verified_name: prev.verified_name || value?.school_name || "",
                  display_phone_number: prev.display_phone_number || value?.admin_phone || "",
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="School *" placeholder="Search school…" />
              )}
            />
            <TextField
              label="WABA ID *"
              value={form.waba_id}
              onChange={(e) => setForm((p) => ({ ...p, waba_id: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Phone Number ID *"
              value={form.phone_number_id}
              onChange={(e) => setForm((p) => ({ ...p, phone_number_id: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Display phone number"
              value={form.display_phone_number}
              onChange={(e) => setForm((p) => ({ ...p, display_phone_number: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Verified name"
              value={form.verified_name}
              onChange={(e) => setForm((p) => ({ ...p, verified_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Business ID"
              value={form.business_id}
              onChange={(e) => setForm((p) => ({ ...p, business_id: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Connection status</InputLabel>
              <Select
                label="Connection status"
                value={form.connection_status}
                onChange={(e) => setForm((p) => ({ ...p, connection_status: e.target.value }))}
              >
                <MenuItem value="connected">Connected</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
                <MenuItem value="token_error">Token error</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.activate_service}
                  onChange={(e) => setForm((p) => ({ ...p, activate_service: e.target.checked }))}
                />
              }
              label="Activate WhatsApp Automation service for this school"
            />
            <TextField
              label="Remark"
              value={form.remark}
              onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: WA, "&:hover": { bgcolor: WA_DARK } }}
          >
            {saving ? "Saving…" : "Create Account"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default ManageWhatsAppAccounts;
